import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ChevronLeft, ChevronRight, Wand2, Check } from 'lucide-react';
import { YearbookPage, YearbookElement, PageBackground } from '@/types/yearbook';
import { ApiTemplateObject, parseTemplateObjects, parseTemplateToLayoutDef } from '@/lib/parseTemplateObjects';
import TemplatePreview from './TemplatePreview';

interface MagicTheme {
  id?: number;
  name: string;
  [key: string]: any;
}

interface MagicAlbum {
  id: number;
  name: string;
  [key: string]: any;
}

interface MagicTemplate {
  id: number;
  objects_position: ApiTemplateObject[];
  background_image_url?: string;
  trame_background_id?: number;
  [key: string]: any;
}

interface MagicPicture {
  id: number;
  url: string;
  full_url?: string;
  thumbnail?: string;
  ratio?: number;
  [key: string]: any;
}

interface MagicPageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pages: YearbookPage[];
  onApplyMagicPage: (pageIndex: number, elements: YearbookElement[], background?: PageBackground, layoutTemplateId?: number) => void;
  onNavigateToPage: (pageIndex: number) => void;
}

type Step = 'choose' | 'preview' | 'page';

const generateId = () => Math.random().toString(36).substring(2, 11);

const MagicPageDialog: React.FC<MagicPageDialogProps> = ({
  open,
  onOpenChange,
  pages,
  onApplyMagicPage,
  onNavigateToPage,
}) => {
  const [step, setStep] = useState<Step>('choose');
  const [loading, setLoading] = useState(false);

  const [themes, setThemes] = useState<MagicTheme[]>([]);
  const [albums, setAlbums] = useState<MagicAlbum[]>([]);
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const [selectedAlbumId, setSelectedAlbumId] = useState<number | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);

  const [templates, setTemplates] = useState<MagicTemplate[]>([]);
  const [pictures, setPictures] = useState<MagicPicture[]>([]);
  const [currentTemplateIdx, setCurrentTemplateIdx] = useState(0);

  const [chosenTemplateIdx, setChosenTemplateIdx] = useState<number | null>(null);
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setSelectedTheme('');
    setSelectedAlbumId(null);
    setTemplates([]);
    setPictures([]);
    setCurrentTemplateIdx(0);
    setChosenTemplateIdx(null);

    setInitialLoading(true);
    fetch(editorUrl('/load_magical_page'))
      .then((r) => r.json())
      .then((data) => {
        setThemes(data.themes || []);
        setAlbums(data.albums || []);
        setInitialLoading(false);
      })
      .catch(() => setInitialLoading(false));
  }, [open]);

  useEffect(() => {
    const firstInterior = pages.findIndex((p) => !p.coverType && p.itemType !== 'Trombi');
    if (firstInterior !== -1) setSelectedPageIndex(firstInterior);
  }, [pages]);

  const handleStep1Submit = useCallback(() => {
    if (!selectedTheme || selectedAlbumId === null) return;
    setLoading(true);
    fetch(editorUrl('/load_magic_pages'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ album_id: selectedAlbumId, theme: selectedTheme, id: themes.find(t => t.name === selectedTheme)?.id, theme_id: themes.find(t => t.name === selectedTheme)?.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        setTemplates(data.trame_templates || []);
        setPictures(data.possibles_pictures || []);
        setCurrentTemplateIdx(0);
        setStep('preview');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedTheme, selectedAlbumId]);

  const handleChoose = useCallback((idx: number) => {
    setChosenTemplateIdx(idx);
    setStep('page');
  }, []);

  const handleValidate = useCallback(() => {
    if (chosenTemplateIdx === null) return;
    const tpl = templates[chosenTemplateIdx];
    if (!tpl) return;

    const shuffled = [...pictures].sort(() => Math.random() - 0.5);
    let finalPicIdx = 0;
    const objectsWithPhotos = tpl.objects_position.map((obj) => {
      if (
        (obj.object_type === 'picture_template' || obj.object_type === 'picture') &&
        !obj.image_url &&
        shuffled.length > 0
      ) {
        const pic = shuffled[finalPicIdx++ % shuffled.length];
        return { ...obj, image_url: pic.full_url || pic.url || pic.thumbnail, object_type: 'picture' as const };
      }
      return obj;
    });

    const { elements, background: parsedBg } = parseTemplateObjects(objectsWithPhotos, generateId);
    const background = tpl.background_image_url
      ? {
          type: 'image' as const,
          value: tpl.background_image_url,
          trame_background_id: tpl.trame_background_id ?? null,
        }
      : parsedBg;

    onApplyMagicPage(selectedPageIndex, elements, background, tpl.id);
    onNavigateToPage(selectedPageIndex);
    onOpenChange(false);
  }, [chosenTemplateIdx, templates, pictures, selectedPageIndex, onApplyMagicPage, onNavigateToPage, onOpenChange]);

  // Build preview def for current template
  const currentPreviewDef = useMemo(() => {
    const tpl = templates[currentTemplateIdx];
    if (!tpl) return null;
    const def = parseTemplateToLayoutDef(tpl.objects_position);
    return {
      ...def,
      backgroundImageUrl: tpl.background_image_url,
      trameBackgroundId: tpl.trame_background_id,
    };
  }, [templates, currentTemplateIdx]);

  const mappedPictures = useMemo(() => {
    return pictures.map((p) => ({ url: p.full_url || p.url || p.thumbnail || '' }));
  }, [pictures]);

  const interiorPages = pages
    .map((p, i) => ({ page: p, index: i }))
    .filter((item) => !item.page.coverType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 size={18} className="text-editor-sidebar-active" />
            Page magique
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Choose theme + album */}
        {step === 'choose' && (
          <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
            {initialLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-muted-foreground" size={24} />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Thème</label>
                  <select
                    value={selectedTheme}
                    onChange={(e) => setSelectedTheme(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Choisir un thème…</option>
                    {themes.map((t, i) => (
                      <option key={t.id ?? i} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Album</label>
                  <select
                    value={selectedAlbumId ?? ''}
                    onChange={(e) => setSelectedAlbumId(Number(e.target.value))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Choisir un album…</option>
                    {albums.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleStep1Submit}
                  disabled={!selectedTheme || selectedAlbumId === null || loading}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
                  Générer les modèles
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Preview templates one by one */}
        {step === 'preview' && (
          <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Aucun modèle disponible pour cette sélection.
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground text-center">
                  Modèle {currentTemplateIdx + 1} / {templates.length}
                </p>

                <div className="max-w-[200px] mx-auto w-full">
                  {currentPreviewDef && (
                    <div className="rounded-md shadow-md border border-editor-sidebar-border overflow-hidden">
                      <TemplatePreview
                        def={currentPreviewDef}
                        pictures={mappedPictures}
                        showTextContent
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setCurrentTemplateIdx((p) => Math.max(0, p - 1))}
                    disabled={currentTemplateIdx <= 0}
                    className="p-2 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => handleChoose(currentTemplateIdx)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                  >
                    <Check size={16} />
                    Choisir
                  </button>
                  <button
                    onClick={() => setCurrentTemplateIdx((p) => Math.min(templates.length - 1, p + 1))}
                    disabled={currentTemplateIdx >= templates.length - 1}
                    className="p-2 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                <button
                  onClick={() => setStep('choose')}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
                >
                  ← Revenir à la sélection
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 3: Choose page */}
        {step === 'page' && (
          <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
            <p className="text-sm text-center text-muted-foreground">
              Sur quelle page appliquer ce modèle ?
            </p>

            <select
              value={selectedPageIndex}
              onChange={(e) => setSelectedPageIndex(Number(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {interiorPages.map((item) => {
                const isTrombi = item.page.itemType === 'Trombi';
                return (
                  <option key={item.page.id} value={item.index} disabled={isTrombi} className={isTrombi ? 'text-muted-foreground' : ''}>
                    Page {item.page.pageNumber}{isTrombi ? ' (Trombi)' : ''}{!isTrombi ? (() => { const c = item.page.elements.filter(e => e.type === 'image').length; return c > 0 ? ` · ${c} photo${c > 1 ? 's' : ''}` : ''; })() : ''}{item.page.name ? ` – ${item.page.name}` : ''}
                  </option>
                );
              })}
            </select>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('preview')}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium border border-input bg-background hover:bg-muted transition-colors"
              >
                Retour
              </button>
              <button
                onClick={handleValidate}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                <Wand2 size={16} />
                Appliquer
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MagicPageDialog;
